import { useState, useRef, useEffect, useMemo } from 'react';
import { message, Space } from 'antd';
import styled from '@emotion/styled';
import { useTranslate } from '@tolgee/react';
import { calculatePackagePrice } from 'services/api/pay';
import { find, isUndefined } from 'lodash-es';
import { PackageSelectPanel } from './PackageSelectPanel';
import { DurationSelectPanel } from './DurationSelectPanel';
import { PanelTitle } from './Common';
import { packageIdLabel, catalogPackageCurrencies } from '../catalogAdapter';
import { narrowPriceZone } from '../priceZone';
import { getActiveAnnualDuration } from '../annualActivity';
import { logPricing } from '../pricingLogger';

const noAddTime: Pay.DurationDescV2 = {
  id: 0,
  name: '0',
  price: 0,
  durationNum: 0,
  currencyAmount: [],
};

/** 连续包月（自动续费订阅）时长项的 id */
const SUBSCRIPTION_DURATION_ID = 5 as Pay.DurationId;

export interface PackageDetailProps {
  orderType: Pay.OrderType;
  orgId: number;
  currency: General.Currency;
  /** 币种是否锁定（升级）：锁定币种是后端对升级的强制口径，越界也照发报价由后端裁决 */
  currencyLocked?: boolean;
  /** 已支付货币（price-zones LAST_PURCHASE）：续费并入币种候选，与 PackageDlg.zoneCurrencies 同源 */
  lastPurchaseCurrency?: General.Currency;
  orderedPackages: Pay.OrderedPackage[];
  packageInfo: Pay.PackageInfoV2;
  checkedPackageId?: Pay.PackageId;
  curWorkingPackage?: Pay.OrderedPackage;
  /** 升级禁选档位（不高于当前档位的目录套餐 ID，续订不传） */
  disabledUpgradeIds?: string[];
  setAccountNum: React.Dispatch<React.SetStateAction<number>>;
  setCanOrder: React.Dispatch<React.SetStateAction<boolean>>;
  isEnterprise: boolean;
  setIsEnterprise: React.Dispatch<React.SetStateAction<boolean>>;
  onPackagePriceChange?: (value?: Pay.CalculatePackagePriceRst) => void;
  onPackageChange?: (value: Pay.PackageId | undefined) => void;
  onDurationChange?: (value: Pay.DurationNum | -1) => void;
  setIsSubscription?: React.Dispatch<React.SetStateAction<boolean>>;
}

// 升级/续订套餐
function PackageDlgDetail(props: PackageDetailProps) {
  const {
    packageInfo,
    setAccountNum,
    checkedPackageId,
    orderedPackages,
    orgId,
    currency,
    currencyLocked,
    lastPurchaseCurrency,
    orderType,
    setCanOrder,
    isEnterprise,
    setIsEnterprise,
    curWorkingPackage,
    disabledUpgradeIds,
    onPackagePriceChange,
    onPackageChange,
    onDurationChange,
    setIsSubscription,
  } = props;

  const { t } = useTranslate();

  const subscriptionItem: Pay.DurationDescV2 = {
    id: SUBSCRIPTION_DURATION_ID,
    name: t('连续包月'),
    durationNum: -1,
    subscription: true,
    price: 0,
    currencyAmount: [],
  };

  // 当前用户是否为订阅用户（连续包月）
  const isWorkingSubscription = curWorkingPackage?.autoRenewing === true;
  // 订阅用户升级只选目标套餐，不显示时长选项
  const hiddenDuration = orderType === 'UPGRADE' && isWorkingSubscription;

  // 将「连续包月」选项插入到第一个时长项之后（升级默认首位是不增加时长）；
  // 非订阅用户升级不提供切订阅入口，不注入连续包月；订阅用户升级虽隐藏时长面板，但仍注入以按订阅时长计算
  const injectSubscriptionItem = (desc: Pay.DurationDescV2[]) => {
    if (!desc.length) return desc;
    if (orderType === 'UPGRADE' && !isWorkingSubscription) {
      return desc;
    }
    return [desc[0], subscriptionItem, ...desc.slice(1)];
  };

  const { packageData, recommendData } = packageInfo;
  const { recommendPackageId, recommendDurationId } = recommendData;
  const curRenewPackage = orderedPackages.find((v) => v.status === 0);

  const getDefaultDurationId = (pkg?: Pay.PackageV2): Pay.DurationId => {
    if (orderType === 'UPGRADE' && isWorkingSubscription) {
      return SUBSCRIPTION_DURATION_ID;
    }
    if (curRenewPackage) {
      return orderType === 'UPGRADE' ? 0 : SUBSCRIPTION_DURATION_ID;
    }
    const annualDuration = getActiveAnnualDuration(packageInfo.priceZone, pkg);
    if (annualDuration) {
      return annualDuration.id;
    }
    return orderType === 'UPGRADE' ? 0 : SUBSCRIPTION_DURATION_ID;
  };

  const [chkDuration, setChkDuration] = useState(() =>
    getDefaultDurationId(packageData.find((pkg) => pkg.id === checkedPackageId)),
  );
  const chkExtraDurationNum = useRef(0);

  // 选中套餐的时长选项（派生自目录，目录刷新引用变化不触发重报价）
  const durationDesc = useMemo(() => {
    const checkedAccount = packageData.find((account) => account.id === checkedPackageId);
    if (!curWorkingPackage || !checkedAccount) {
      return [];
    }
    // 升级/续订均支持选择自动续费（连续包月），升级首位默认不增加时长
    return injectSubscriptionItem(
      orderType === 'UPGRADE'
        ? [noAddTime, ...checkedAccount.durationDesc]
        : checkedAccount.durationDesc,
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [packageData, checkedPackageId, curWorkingPackage, orderType]);

  // 报价入参的语义依赖项（标量）
  const checkedDuration = find(durationDesc, ['id', chkDuration]);
  const checkedDurationNum = checkedDuration?.durationNum;
  useEffect(() => {
    if (checkedDurationNum !== undefined) {
      setIsSubscription?.(checkedDurationNum === -1);
    }
  }, [checkedDurationNum, setIsSubscription]);
  const regionPackageId = useMemo(
    () => find(packageData, ['id', checkedPackageId])?.regionPackageId,
    [packageData, checkedPackageId],
  );
  // 选中套餐的候选币种（CN 价区强制人民币；续费并入已支付货币——老套餐可能以任意货币
  // 支付过，续费允许沿用；与 PackageDlg 的 zoneCurrencies 同源，缺一会导致误停价）
  const currencyCandidates = useMemo(() => {
    const base =
      narrowPriceZone(packageInfo.priceZone) === 'CN'
        ? (['CNY'] as General.Currency[])
        : catalogPackageCurrencies(find(packageData, { id: checkedPackageId }));
    if (
      orderType === 'RENEW' &&
      lastPurchaseCurrency &&
      lastPurchaseCurrency !== 'CNY' &&
      !base.includes(lastPurchaseCurrency)
    ) {
      return [lastPurchaseCurrency, ...base];
    }
    return base;
  }, [packageData, checkedPackageId, packageInfo.priceZone, orderType, lastPurchaseCurrency]);
  // 报价只依赖实际参数和可报价状态，目录/套餐对象刷新不应重复发送相同报价。
  const hasWorkingPackage = !!curWorkingPackage;
  const currencyAllowed =
    !!currencyLocked || !currencyCandidates.length || currencyCandidates.includes(currency);
  const catalogRevision = packageInfo.revision;

  // 重新计算价格（ignore 标记防止过期响应覆盖新报价）
  useEffect(() => {
    if (!hasWorkingPackage || !checkedPackageId || checkedDurationNum === undefined) {
      return;
    }
    // 币种越界不报价仅约束可切换场景（续订/未锁定升级——候选恒含当前币种，实际不触发）。
    // 锁定币种是后端对升级的强制口径（升级不可变更币种），目录候选缺它（老套餐回退 IP 价区、
    // 目录未配该币种等）时必须照发报价由后端裁决，前端停价会静默掩盖整个报价区
    if (!currencyAllowed) {
      logPricing('quote_currency_blocked', {
        orgId,
        orderAction: orderType,
        priceZone: packageInfo.priceZone,
        regionPackageId,
        currency,
        currencyCandidates,
      });
      setCanOrder(false);
      onPackagePriceChange?.(undefined);
      return;
    }
    chkExtraDurationNum.current = checkedDuration?.extraDurationNum ?? 0;
    let ignore = false;
    const durationNum = checkedDurationNum;
    // 目录数据（新价格体系）按 regionPackageId + 目录版本报价；
    // 连续包月报价与下单同参，durationNum 统一传 -1
    const params =
      regionPackageId && catalogRevision
        ? {
            orgId,
            regionPackageId,
            catalogRevision,
            currency,
            orderType,
            durationNum,
          }
        : {
            orgId,
            orderType,
            durationNum,
            newPackageId: checkedPackageId,
            currency,
          };
    const logContext = {
      ...params,
      selectedPriceZone: packageInfo.priceZone,
      displayedPackageName: find(packageData, ['id', checkedPackageId])?.displayName,
      currentPackageId: curWorkingPackage?.id,
      currentRegionPackageId: curWorkingPackage?.regionPackageId,
      currencyLocked,
    };
    const startedAt = Date.now();
    logPricing('quote_requested', logContext);
    calculatePackagePrice(params)
      .then((d) => {
        logPricing('quote_succeeded', {
          ...logContext,
          elapsedMs: Date.now() - startedAt,
          superseded: ignore,
          payPrice: d.payPrice,
          reducePrice: d.reducePrice,
          beforeValidStart: d.beforeValidStart,
          beforeValidEnd: d.beforeValidEnd,
          afterValidStart: d.afterValidStart,
          afterValidEnd: d.afterValidEnd,
        });
        if (ignore) return;
        onDurationChange?.(durationNum);
        setCanOrder(true);
        onPackagePriceChange?.(d);
      })
      .catch((e) => {
        logPricing(
          'quote_failed',
          { ...logContext, elapsedMs: Date.now() - startedAt, superseded: ignore },
          e,
        );
        if (ignore) return;
        message.error(`/calculatePackagePrice ${e?.message}`);
        setCanOrder(false);
        onPackagePriceChange?.(undefined);
      });
    return () => {
      ignore = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    checkedPackageId,
    checkedDurationNum,
    orgId,
    hasWorkingPackage,
    orderType,
    currency,
    currencyAllowed,
    regionPackageId,
    catalogRevision,
  ]);

  return curWorkingPackage ? (
    <Space size={16} direction="vertical" className="w-full">
      <PanelTitle>
        {t('market.当前套餐')}
        <PackageDesc>
          {packageIdLabel(
            curWorkingPackage.id,
            t,
            curWorkingPackage.comboName,
            curWorkingPackage.regionPackageId,
          )}
        </PackageDesc>
      </PanelTitle>
      <PackageSelectPanel
        value={checkedPackageId}
        isEnterprise={isEnterprise}
        recommendPackageId={orderType === 'UPGRADE' ? undefined : recommendPackageId}
        list={packageData}
        disabled={(_, item) =>
          // 升级只升不降：不高于当前档位置灰禁选（旧位次判定依赖老版数字 id，目录序号下失效）
          orderType === 'UPGRADE' &&
          !!item?.regionPackageId &&
          !!disabledUpgradeIds?.includes(item.regionPackageId)
        }
        onChange={(isEnterprise, selected) => {
          setIsEnterprise(isEnterprise);
          if (isEnterprise) {
            // 企业版不支持自动续费
            setIsSubscription?.(false);
            onPackageChange?.(undefined);
          } else if (selected) {
            const { accountNum, id } = selected;
            setAccountNum(accountNum);
            // 时长选项随 checkedPackageId 变化由上方 memo 派生
            onPackageChange?.(id);
            const durationId = getDefaultDurationId(selected);
            setChkDuration(durationId);
            setIsSubscription?.(durationId === SUBSCRIPTION_DURATION_ID);
          }
        }}
      />
      {/* 订阅用户升级只选目标套餐，不显示时长选项；其余场景正常显示 */}
      {!hiddenDuration && durationDesc.length ? (
        <DurationSelectPanel
          priceZone={packageInfo.priceZone}
          durationId={chkDuration}
          packageId={checkedPackageId}
          orderType={orderType}
          currency={currency}
          packageData={packageData}
          recommendDurationId={orderType === 'UPGRADE' ? undefined : recommendDurationId}
          canIncreaseDuration={!curRenewPackage}
          list={durationDesc}
          disabled={(value) => !isUndefined(curRenewPackage) && value.id > 0}
          onChange={(value) => {
            setChkDuration(value.id);
            setIsSubscription?.(!!value.subscription);
          }}
        />
      ) : null}
    </Space>
  ) : null;
}

export default PackageDlgDetail;

export const Detail = styled.section`
  margin-bottom: 24px;
  h4 {
    display: flex;
    align-items: center;
    height: 20px;
    margin-bottom: 16px;
    font-size: 14px;
    font-weight: normal;
    color: var(--fett-text-primary);
    span.packtip {
      margin-left: 8px;
      font-size: 12px;
      color: var(--fett-surface-warning-text);
      background: #faedd2;
      padding: 0 4px;
      border-radius: 2px;
    }
  }
`;
const PackageDesc = styled.span`
  color: var(--fett-text-secondary);
  font-weight: 400;
  margin-left: 8px;
`;
